<script setup lang="ts">
/**
 * Open, Export and Share for the `documentActions` slot of NxOfficeRibbon.
 *
 * The export menu follows the menu-button pattern: a click or Arrow Down opens
 * it with the first entry focused, the arrows, Home and End move inside it,
 * Escape hands focus back to the button, and Tab closes it and moves on to
 * whatever follows the button. NxDropdown leaves dismissal to its consumer, so
 * the outside press is handled here.
 */
import { nextTick, onBeforeUnmount, ref, useId } from 'vue';
import ChevronDown from 'lucide-vue-next/dist/esm/icons/chevron-down.js'; import Share2 from 'lucide-vue-next/dist/esm/icons/share-2.js';
import NxOfficeIcon from './NxOfficeIcon.vue';
import NxButton from '../Button/NxButton.vue';
import { NxMenuDropdown, NxMenuItem } from '../Menu';
import '../office-view.css';

export interface NxOfficeExportItem {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
}

withDefaults(
  defineProps<{
    openLabel: string;
    /** Accessible name of Open when it has to say more than the visible label. */
    openAriaLabel?: string;
    exportLabel: string;
    exportItems: readonly NxOfficeExportItem[];
    shareLabel?: string;
    shareAriaLabel?: string;
    showShare?: boolean;
  }>(),
  { showShare: false }
);

const emit = defineEmits<{ open: []; export: [id: string]; share: [] }>();

const menuId = useId();
const exportOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);

function trigger(): HTMLButtonElement | null {
  return rootRef.value?.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]') ?? null;
}

function entries(): HTMLButtonElement[] {
  return [...(menuRef.value?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
}

function onOutsidePointerDown(event: PointerEvent): void {
  const target = event.target as Node | null;
  if (target && rootRef.value?.contains(target)) return;
  closeMenu();
}

async function openMenu(): Promise<void> {
  if (exportOpen.value) return;
  exportOpen.value = true;
  window.addEventListener('pointerdown', onOutsidePointerDown, true);
  // NxDropdown shows its popover a tick after `isOpen` changes, and an entry
  // inside a popover that is still closed cannot take focus.
  await nextTick();
  await nextTick();
  entries()[0]?.focus({ preventScroll: true });
}

function closeMenu(): void {
  if (!exportOpen.value) return;
  exportOpen.value = false;
  window.removeEventListener('pointerdown', onOutsidePointerDown, true);
}

function toggleMenu(): void {
  if (exportOpen.value) closeMenu();
  else void openMenu();
}

function onMenuKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeMenu();
    trigger()?.focus();
    return;
  }
  if (event.key === 'Tab') {
    // Not prevented: from the button, the browser moves on to the next control.
    trigger()?.focus();
    closeMenu();
    return;
  }
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  const items = entries();
  if (!items.length) return;
  const index = items.indexOf(document.activeElement as HTMLButtonElement);
  const next =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? items.length - 1
        : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
  event.preventDefault();
  items[next]?.focus();
}

function choose(id: string): void {
  closeMenu();
  trigger()?.focus();
  emit('export', id);
}

onBeforeUnmount(closeMenu);
</script>

<template>
  <div ref="rootRef" class="nx-office-document-actions">
    <NxButton variant="ghost" height="xs" :aria-label="openAriaLabel" @click="emit('open')">
      <NxOfficeIcon name="open" />
      <span>{{ openLabel }}</span>
    </NxButton>
    <NxMenuDropdown :is-open="exportOpen" align="end" @close="closeMenu">
      <template #trigger>
        <NxButton
          variant="ghost"
          height="xs"
          :aria-label="exportLabel"
          aria-haspopup="menu"
          :aria-expanded="exportOpen"
          :aria-controls="menuId"
          @click="toggleMenu"
          @keydown.down.prevent="openMenu"
        >
          <NxOfficeIcon name="download" />
          <span>{{ exportLabel }}</span>
          <ChevronDown class="nxt-icon-xs" aria-hidden="true" />
        </NxButton>
      </template>
      <div
        :id="menuId"
        ref="menuRef"
        class="nx-office-document-actions__menu"
        role="menu"
        :aria-label="exportLabel"
        @keydown="onMenuKeydown"
      >
        <NxMenuItem
          v-for="item in exportItems"
          :key="item.id"
          role="menuitem"
          tabindex="-1"
          :disabled="item.disabled"
          @select="choose(item.id)"
        >
          {{ item.label }}
        </NxMenuItem>
      </div>
    </NxMenuDropdown>
    <NxButton
      v-if="showShare"
      variant="default"
      height="xs"
      :aria-label="shareAriaLabel"
      @click="emit('share')"
    >
      <!-- The one filled button of the header: the icon takes its text colour,
           a coloured office icon would vanish on the brand fill. -->
      <Share2 class="nxt-icon-md" aria-hidden="true" />
      <span>{{ shareLabel }}</span>
    </NxButton>
  </div>
</template>
